import { getDashboardData } from "@/api/deals-api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/store/auth-store";
import { useWorkspaces, useWorkspaceStore } from "@/store/workspace-store";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Loader2,
  LogOut,
  Plus,
  Search,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { CreateWorkspaceDialog } from "./create-workspace-dialog";
import { useOrgQuota } from "@/hooks/use-org-quota";
import { useMe } from "@/hooks/use-me";

export default function ListWorkspacesComponent() {
  const { data: workspaces, isLoading, error } = useWorkspaces();
  const { activeWorkspace, setActiveWorkspaceId } = useWorkspaceStore();
  const { logout } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { quota } = useOrgQuota();
  // Fail closed: only surface the create affordance once quota has loaded and
  // confirms headroom. While the query is loading (e.g. after a hard refresh
  // wipes the cache) quota is undefined — gating on "not at cap" would render
  // the button during that window and let a capped user create over the limit.
  const canCreateWorkspace =
    !!quota && quota.workspaces_used < quota.workspaces_cap;

  // A logged-in user with a company but no workspace is a founder/startup
  // account (the investor/startup split: investors own workspaces, founders
  // don't). Founders belong in the startup portal, not this investor app, so
  // bounce them there instead of offering to create an investor workspace.
  const { company, isLoading: isMeLoading, isError: isMeError } = useMe();
  const isFounderAccount =
    !isLoading && !error && (workspaces?.length ?? 0) === 0 && !!company;

  useEffect(() => {
    if (isFounderAccount) {
      window.location.href =
        import.meta.env.VITE_STARTUPFUEL_APP_URL ||
        "https://app.startupfuel.com";
    }
  }, [isFounderAccount]);

  // Per-workspace deal counts via react-query: independent caching, retries,
  // and progressive render — each card's badge resolves when its own query
  // settles instead of waiting on the slowest workspace.
  const dealCountResults = useQueries({
    queries: (workspaces ?? []).map((workspace) => ({
      queryKey: ["workspace-deal-count", workspace._id],
      queryFn: async () => {
        const dashboardData = await getDashboardData(workspace._id);
        return dashboardData.data?.total_deals ?? 0;
      },
      staleTime: 60_000,
    })),
  });

  const dealCountByWorkspace = useMemo(() => {
    const map: Record<
      string,
      { count?: number; isLoading: boolean; isError: boolean }
    > = {};
    (workspaces ?? []).forEach((workspace, idx) => {
      const result = dealCountResults[idx];
      const isError = result?.isError ?? false;
      map[workspace._id] = {
        count: isError ? undefined : result?.data,
        isLoading: result?.isLoading ?? false,
        isError,
      };
    });
    return map;
  }, [workspaces, dealCountResults]);

  const handleWorkspaceChange = async (workspaceId: string) => {
    if (switchingId) return;
    setSwitchingId(workspaceId);

    try {
      setActiveWorkspaceId(workspaceId);
      await new Promise((resolve) => setTimeout(resolve, 50));

      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.some(
            (el) =>
              typeof el === "string" &&
              (el.includes("deal") ||
                el.includes("deals") ||
                el.includes("workspace") ||
                el.includes("reports"))
          ),
      });

      navigate(`/${workspaceId}/dashboard`);
    } catch (err) {
      console.error("Failed to change workspace:", err);
    } finally {
      setSwitchingId(null);
    }
  };

  const filteredWorkspaces = useMemo(() => {
    if (!workspaces) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return workspaces;
    return workspaces.filter((w) =>
      w.opportunity_name.toLowerCase().includes(q)
    );
  }, [workspaces, searchQuery]);

  // ─── Loading ─────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-full bg-background">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
          <div className="mb-6 sm:mb-8 space-y-3">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96 max-w-full" />
          </div>
          <Skeleton className="h-10 w-full sm:w-80 mb-6" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl border bg-card p-5 space-y-4"
              >
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="h-5 w-20 rounded-full" />
                </div>
                <Skeleton className="h-9 w-full" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── Error ───────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-full bg-background flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2 text-destructive mb-2">
              <AlertCircle className="h-5 w-5" />
              <span className="text-sm font-medium">Error</span>
            </div>
            <CardTitle>Failed to load workspaces</CardTitle>
            <CardDescription>{error.message}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={() => window.location.reload()} className="w-full">
              Try again
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // ─── Empty ───────────────────────────────────────────────────────
  if (!workspaces?.length) {
    // Wait for /me before deciding, then redirect founders to the portal (see
    // effect above) instead of showing the investor "create workspace" card.
    if (isMeLoading || isFounderAccount) {
      return (
        <div className="min-h-full bg-background flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    // Founder vs investor is decided by /me; if it failed we can't tell, so
    // surface the error instead of defaulting to the investor create card and
    // mis-routing a founder.
    if (isMeError) {
      return (
        <div className="min-h-full bg-background flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-2 text-destructive mb-2">
                <AlertCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Error</span>
              </div>
              <CardTitle>Failed to load your account</CardTitle>
              <CardDescription>
                Please try again. If this keeps happening, contact support.
              </CardDescription>
            </CardHeader>
            <CardFooter>
              <Button onClick={() => window.location.reload()} className="w-full">
                Try again
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }
    return (
      <>
        <div className="min-h-full bg-background flex items-center justify-center px-4 py-12">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-2 text-amber-600 mb-2">
                <Briefcase className="h-5 w-5" />
                <span className="text-sm font-medium">No workspaces</span>
              </div>
              <CardTitle>You don't have any workspaces yet</CardTitle>
              <CardDescription>
                Create one to get started, or contact your administrator to be
                added to an existing workspace.
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex flex-col sm:flex-row gap-2">
              {canCreateWorkspace && (
                <Button
                  className="w-full sm:flex-1"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create workspace
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full sm:flex-1"
                onClick={() => logout()}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </Button>
            </CardFooter>
          </Card>
        </div>
        <CreateWorkspaceDialog
          open={showCreateDialog}
          onOpenChange={setShowCreateDialog}
          onSuccess={() => window.location.reload()}
        />
      </>
    );
  }

  // ─── Loaded ──────────────────────────────────────────────────────
  return (
    <>
      <div className="min-h-full bg-background">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
          {/* Back to active workspace */}
          {activeWorkspace && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                navigate(`/${activeWorkspace._id}/dashboard`)
              }
              className="-ml-2 mb-4 h-8 px-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Back to {activeWorkspace.opportunity_name}
            </Button>
          )}

          {/* Header */}
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6 sm:mb-8">
            <div className="space-y-1.5">
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                Your workspaces
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Select a workspace to continue working with your deals and
                reports.
              </p>
            </div>
            {canCreateWorkspace && (
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="w-full sm:w-auto shrink-0"
              >
                <Plus className="mr-2 h-4 w-4" />
                New workspace
              </Button>
            )}
          </div>

          {/* Search — only show if there are enough workspaces to warrant it */}
          {workspaces.length > 4 && (
            <div className="relative mb-6 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search workspaces..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          )}

          {/* Empty filter result */}
          {filteredWorkspaces.length === 0 ? (
            <div className="rounded-xl border bg-card p-12 text-center text-muted-foreground">
              <Search className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No workspaces match "{searchQuery}"</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
              {filteredWorkspaces.map((workspace) => {
                const daysAgo = formatDistanceToNow(
                  new Date(workspace.created_on),
                  { addSuffix: true }
                );
                const dealCountInfo = dealCountByWorkspace[workspace._id];
                const dealCount = dealCountInfo?.count;
                const isDealCountLoading = dealCountInfo?.isLoading;
                const isDealCountError = dealCountInfo?.isError;
                const isCurrentlySwitching = switchingId === workspace._id;

                return (
                  <Card
                    key={workspace._id}
                    className="group flex flex-col transition-all duration-150 hover:border-foreground/20 hover:shadow-md"
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                          <span className="text-base font-semibold leading-none">
                            {workspace.opportunity_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-base truncate">
                            {workspace.opportunity_name}
                          </CardTitle>
                          <CardDescription className="text-xs mt-0.5">
                            Created {daysAgo}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="pb-4 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        {isDealCountLoading ? (
                          <Skeleton className="h-5 w-16 rounded-full" />
                        ) : isDealCountError ? (
                          <Badge
                            variant="outline"
                            className="font-normal text-xs text-muted-foreground"
                          >
                            Deal count unavailable
                          </Badge>
                        ) : (
                          <Badge
                            variant="secondary"
                            className="font-normal text-xs"
                          >
                            {dealCount ?? 0}{" "}
                            {dealCount === 1 ? "deal" : "deals"}
                          </Badge>
                        )}
                        {workspace.role && (
                          <Badge
                            variant="outline"
                            className="font-normal text-xs capitalize"
                          >
                            {workspace.role}
                          </Badge>
                        )}
                      </div>
                    </CardContent>

                    <CardFooter className="pt-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={isCurrentlySwitching}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleWorkspaceChange(workspace._id);
                        }}
                      >
                        {isCurrentlySwitching ? (
                          "Opening..."
                        ) : (
                          <>
                            Open
                            <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                          </>
                        )}
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <CreateWorkspaceDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={() => window.location.reload()}
      />
    </>
  );
}
